import { forwardRef, type HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

const SIZE_CLASS = {
  xs: "size-5 [&_svg]:size-3",
  sm: "size-6 [&_svg]:size-3.5",
  md: "size-8 [&_svg]:size-[18px]",
  lg: "size-10 [&_svg]:size-5",
  xl: "size-12 [&_svg]:size-6",
} as const;

export interface ThemeIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: keyof typeof SIZE_CLASS;
  variant?: "filled" | "light";
  /**
   * Token de color · default `"multiva"` (regla global: TODOS los chips van
   * naranja+blanco). Acepta rampa sola (`"multiva"` → usa el tono 600) o
   * rampa+tono explícito (`"multiva-800"`) para paletas monocromáticas
   * diferenciadas por tono, como los KPIs del dashboard.
   */
  color?: string;
  radius?: "sm" | "md" | "xl";
}

/**
 * Reemplazo de `<ThemeIcon>` de Mantine · chip cuadrado/circular para un
 * ícono. `globals.css` fuerza HOY todos los `ThemeIcon` a naranja+blanco
 * (`--ti-bg`/`--ti-color` con `!important`) sin importar `color`/`variant` —
 * acá el default (`color="multiva"`, `variant="filled"`) YA reproduce ese
 * look, así que no hace falta el "martillazo" global.
 */
export const ThemeIcon = forwardRef<HTMLDivElement, ThemeIconProps>(
  ({ size = "md", variant = "filled", color = "multiva", radius = "md", className, style, ...props }, ref) => {
    const conTono = /-\d{2,3}$/.test(color);
    const base = conTono ? `var(--color-${color})` : `var(--color-${color}-600)`;
    const glifoLight = conTono ? base : `var(--color-${color}-700)`;
    return (
      <div
        ref={ref}
        className={cn(
          "inline-flex shrink-0 items-center justify-center",
          radius === "sm" ? "rounded-sm" : radius === "xl" ? "rounded-xl" : "rounded-md",
          SIZE_CLASS[size],
          className
        )}
        style={{
          backgroundColor: variant === "filled" ? base : `color-mix(in srgb, ${base} 15%, transparent)`,
          color: variant === "filled" ? "#fff" : glifoLight,
          ...style,
        }}
        {...props}
      />
    );
  }
);
ThemeIcon.displayName = "ThemeIcon";
